import { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Custom hook for preventing redundant navigation and managing navigation state
 * @param {Object} options - Configuration options
 * @param {boolean} options.preventSameRoute - Prevent navigation to current route (default: true)
 * @param {number} options.debounceDelay - Debounce delay for navigation (default: 200)
 * @returns {Object} - Navigation utilities
 */
const useNavigationGuard = (options = {}) => {
    const { preventSameRoute = true, debounceDelay = 200 } = options;
    const location = useLocation();
    const navigate = useNavigate();
    const lastNavigationTime = useRef(0);
    const isNavigating = useRef(false);

    /**
     * Check if the target path is the same as current location
     * @param {string} targetPath - The path to navigate to
     * @returns {boolean} - True if paths are the same
     */
    const isSameRoute = useCallback((targetPath) => {
        // Normalize paths for comparison
        const currentPath = location.pathname;
        const normalizedTarget = targetPath.startsWith('/') ? targetPath : `/${targetPath}`;
        const normalizedCurrent = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
        
        return normalizedCurrent === normalizedTarget;
    }, [location.pathname]);

    /**
     * Safe navigation function with guards
     * @param {string} path - The path to navigate to
     * @param {Object} options - Navigation options
     * @returns {boolean} - True if navigation was allowed, false if prevented
     */
    const guardedNavigate = useCallback((path, navigationOptions = {}) => {
        const now = Date.now();
        const timeSinceLastNavigation = now - lastNavigationTime.current;

        // Prevent rapid navigation attempts
        if (timeSinceLastNavigation < debounceDelay) {
            console.log('Navigation prevented: too rapid');
            return false;
        }

        // Prevent navigation to same route if option is enabled
        if (preventSameRoute && isSameRoute(path)) {
            console.log('Navigation prevented: same route', { current: location.pathname, target: path });
            return false;
        }

        // Prevent navigation if already navigating
        if (isNavigating.current) {
            console.log('Navigation prevented: already navigating');
            return false;
        }

        // Update tracking variables
        lastNavigationTime.current = now;
        isNavigating.current = true;

        try {
            navigate(path, navigationOptions);
            
            // Reset navigation flag after a short delay
            setTimeout(() => {
                isNavigating.current = false;
            }, 500);

            return true;
        } catch (error) {
            console.error('Navigation error:', error);
            isNavigating.current = false;
            return false;
        }
    }, [navigate, location.pathname, preventSameRoute, debounceDelay, isSameRoute]);

    /**
     * Check if navigation to a path would be allowed
     * @param {string} path - The path to check
     * @returns {boolean} - True if navigation would be allowed
     */
    const canNavigate = useCallback((path) => {
        const now = Date.now();
        const timeSinceLastNavigation = now - lastNavigationTime.current;

        if (timeSinceLastNavigation < debounceDelay) return false;
        if (preventSameRoute && isSameRoute(path)) return false;
        if (isNavigating.current) return false;

        return true;
    }, [debounceDelay, preventSameRoute, isSameRoute]);

    /**
     * Get current navigation state
     * @returns {Object} - Navigation state information
     */
    const getNavigationState = useCallback(() => ({
        isNavigating: isNavigating.current,
        currentPath: location.pathname,
        lastNavigationTime: lastNavigationTime.current,
    }), [location.pathname]);

    return {
        guardedNavigate,
        canNavigate,
        isSameRoute,
        getNavigationState,
        isNavigating: isNavigating.current,
        currentPath: location.pathname,
    };
};

export default useNavigationGuard;
